import {
	ButtonWithLoading,
	ErrorModal,
	MesfModal,
	getMomentTz,
} from "@dexteel/mesf-core";
import { Button, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import React, { useEffect, useState } from "react";

import { Process } from "../../../caster/models/ProcessGanttChart";
import { UnLinkedHeat } from "../../../caster/models/UnLinkedHeat";
import {
	linkCasterProcess,
	searchUnlinkedHeats as searchCasterUnlinkedHeats,
} from "../../../caster/repositories/ProductionRepository";
import {
	linkEAFProcess,
	searchUnlinkedHeats as searchEAFUnlinkedHeats,
} from "../../../eaf/eaf-processes/repositories/EAFRepository";

const moment = getMomentTz();

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	process: Process | null;
};
export const LinkProcess = ({ show, onHide, process }: Props) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [unlinkedHeats, setUnLinkedHeats] = useState<UnLinkedHeat[]>([]);
	const [selectedHeat, setSelectedHeat] = useState<any>(null);
	const [searchText, setSearchText] = useState("");
	const [error, setError] = useState("");
	const [showWarningModal, setShowWarningModal] = useState(false);

	const isCaster = process?.ProcessCode === "C";
	const title = `Link ${isCaster ? "Caster" : "EAF"} Process`;

	const proceedWithLinking = async () => {
		try {
			setIsSubmitLoading(true);
			const linkProcess = isCaster ? linkCasterProcess : linkEAFProcess;
			const response = await linkProcess(
				process?.ProcessId as number,
				selectedHeat.HeatId,
			);
			if (response.ok) {
				onHide(true);
			} else {
				setError(response.message);
			}
		} catch (e: any) {
			setError(e.message);
		} finally {
			setIsSubmitLoading(false);
			setShowWarningModal(false);
		}
	};

	const onSubmit = async () => {
		if (selectedHeat === null) {
			setError("Please select a heat");
			return;
		}

		if (!process) return;

		if (selectedHeat.IsLinked === 1) {
			setShowWarningModal(true);
		} else {
			await proceedWithLinking();
		}
	};

	const handleSearchChange = async () => {
		if (!process) return;
		const searchUnlinkedHeats = isCaster
			? searchCasterUnlinkedHeats
			: (searchText: string, start: Date, assetId: number) =>
					searchEAFUnlinkedHeats(searchText, start, assetId);

		const heats = await searchUnlinkedHeats(
			searchText,
			process.Start,
			process.AssetId,
		);
		if (heats.ok && heats.data?.tables?.[0]?.rows) {
			setUnLinkedHeats(heats.data.tables[0].rows);
		}
	};

	const handleInputChange = (event: React.ChangeEvent<{}>, value: string) => {
		setSearchText(value);
	};

	const handleKeyDown = async (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			event.preventDefault();
			await handleSearchChange();
		}
	};

	const handleChange = (
		event: React.ChangeEvent<{}>,
		newValue: UnLinkedHeat | null,
	) => {
		setSelectedHeat(newValue as any);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setSelectedHeat(null);
				if (process === undefined || process === null) return;
				const searchUnlinkedHeats = isCaster
					? searchCasterUnlinkedHeats
					: (searchText: string, start: Date, assetId: number) =>
							searchEAFUnlinkedHeats(searchText, start, assetId);
				const heats = await searchUnlinkedHeats(
					"",
					process.Start,
					process.AssetId,
				);
				if (
					heats.ok &&
					heats.data &&
					heats.data.tables &&
					heats.data.tables[0].rows
				) {
					setUnLinkedHeats(heats.data.tables[0].rows);
				}
			})();
		}
	}, [show, process]);

	return (
		<>
			<MesfModal
				maxWidth="xl"
				open={show}
				handleClose={() => {
					onHide(true);
				}}
				id="linkCasterProcess"
				title={title}
			>
				<MesfModal.Content
					style={{
						padding: "15px 30px",
						display: "flex",
						justifyContent: "flex-start",
						alignItems: "center",
					}}
				>
					<Grid container spacing={1} alignItems="center">
						<Grid size={{ xs: 12 }}>
							<Typography>
								<strong>{isCaster ? " Ladle Open" : "Start"}</strong>:{" "}
								{moment(process?.Start).format("YYYY/MM/DD HH:mm")}
							</Typography>
							<Typography>
								{process && process.HeatName
									? "Heat: " + process?.HeatName
									: " "}
							</Typography>
						</Grid>
						<Grid size={{ xs: 12 }}>
							<Autocomplete
								options={unlinkedHeats}
								getOptionLabel={(option) => option.HeatName}
								value={selectedHeat}
								onChange={handleChange}
								inputValue={searchText}
								onInputChange={handleInputChange}
								renderOption={(props, option) => (
									<li {...props}>
										<div style={{ display: "flex", alignItems: "center" }}>
											<span>{option.HeatName}</span>
											{option.IsLinked === 1 && (
												<span style={{ color: "red", marginLeft: "10px" }}>
													(linked)
												</span>
											)}
										</div>
									</li>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										label={searchText ? "Heat" : "Search Heat"}
										placeholder="Search Heat"
										variant="outlined"
										fullWidth
										onKeyDown={handleKeyDown}
										InputLabelProps={{
											style: {
												height: "auto",
												marginTop: 5,
												display: "flex",
												alignItems: "center",
											},
										}}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide(true)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid size={{ xs: 12, md: 3 }} style={{ margin: 0 }}>
							<ButtonWithLoading
								fullWidth
								isLoading={isSubmitLoading}
								disabled={isSubmitLoading}
								variant="contained"
								color="primary"
								onClick={onSubmit}
							>
								Save
							</ButtonWithLoading>
						</Grid>
					</Grid>
				</MesfModal.Actions>
			</MesfModal>

			<MesfModal
				open={showWarningModal}
				handleClose={() => setShowWarningModal(false)}
				title="Warning"
				id="warningModal"
			>
				<MesfModal.Content>
					<p>
						This heat is already linked in another process. Do you want to
						proceed?
					</p>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Button
						fullWidth
						variant="contained"
						color="inherit"
						onClick={() => setShowWarningModal(false)}
					>
						Cancel
					</Button>
					<Button
						fullWidth
						variant="contained"
						color="primary"
						onClick={proceedWithLinking}
					>
						Proceed
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
